import { useCallback, useEffect, useRef } from 'react';
import { Client, Message } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store';
import { addNotification, fetchUnreadCount } from '../store/slices/notificationSlice';
import { toast } from 'react-toastify';
import { featureFlags } from '../config/featureFlags';
import { adminAuthService } from '../services/adminAuthService';

const WEBSOCKET_URL = featureFlags.general.wsUrl + '/ws';
const RECONNECT_DELAY = 5000;
const HEARTBEAT_INCOMING = 10000;
const HEARTBEAT_OUTGOING = 10000;

interface NotificationMessage {
  id?: number;
  title?: string;
  message?: string;
  type?: 'INFO' | 'WARNING' | 'ERROR' | 'SUCCESS';
  status?: 'UNREAD' | 'READ' | 'DELIVERED';
  createdAt?: string;
  senderName?: string;
  isRead?: boolean;
  isDelivered?: boolean;
  notificationType?: string;
  messageStatus?: 'UNREAD' | 'READ' | 'DELIVERED';
  createdDate?: string;
}

interface CloseEvent {
  code: number;
  reason: string;
  wasClean: boolean;
}

export const useWebSocketNotifications = (shouldConnect: boolean = true) => {
  const dispatch = useDispatch();
  const token = useSelector((state: RootState) => state.auth.token);
  const authType = useSelector((state: RootState) => state.auth.type);
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const adminUser = useSelector((state: RootState) => state.auth.adminUser);
  const clientRef = useRef<Client | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const authFailureCountRef = useRef<number>(0);
  const MAX_AUTH_FAILURES = 3; // Stop retrying after 3 consecutive auth failures

  const createClient = useCallback(() => {
    if (!shouldConnect || !isAuthenticated) {
      console.log('WebSocket not connecting:', { shouldConnect, isAuthenticated });
      return null;
    }

    // P0: Admin accounts must not connect to retail /ws (prevents DB pool exhaustion from retry storm)
    const activeAdmin = adminUser ?? adminAuthService.getCurrentAdmin();
    if (activeAdmin?.username) {
      console.log('WebSocket skipped: admin session active for', activeAdmin.username);
      return null;
    }

    console.log('Creating WebSocket client with auth:', { authType });

    const client = new Client({
      webSocketFactory: () => {
        let url = WEBSOCKET_URL;
        
        // For JWT auth, append token to URL only if token is valid and not expired
        if (authType === 'JWT' && token && token !== 'SESSION_AUTH') {
          // Check if token is expired before using it
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const now = Math.floor(Date.now() / 1000);
            const exp = payload.exp;
            
            if (exp && exp > now) {
              // Token is valid, use it
              url = `${url}?access_token=${encodeURIComponent(token)}`;
              console.log('WebSocket URL with JWT token (valid until:', new Date(exp * 1000), ')');
            } else {
              // Token is expired, fall back to session authentication
              console.warn('JWT token is expired, falling back to session authentication for WebSocket');
              console.log('Token expired at:', exp ? new Date(exp * 1000) : 'unknown', ', current time:', new Date());
            }
          } catch (e) {
            console.error('Error parsing JWT token for WebSocket:', e);
            console.log('Falling back to session authentication for WebSocket');
          }
        }
        
        if (authType === 'SESSION' || !url.includes('access_token')) {
          console.log('WebSocket using session authentication (cookies will be sent)');
        }
        
        console.log('Initializing SockJS connection to:', url);
        const socket = new SockJS(url, null, {
          transports: ['websocket', 'xhr-streaming', 'xhr-polling'],
          timeout: 10000,
          withCredentials: true // Always send cookies for session sharing
        });
        
        socket.onopen = () => {
          console.log('SockJS connection opened');
        };
        
        socket.onclose = (event: CloseEvent) => {
          console.log('WebSocket connection closed:', event);
          if (!event.wasClean) {
            console.error('WebSocket connection closed unexpectedly:', event);
            toast.error('Connection closed unexpectedly');
          }
        };
        
        socket.onerror = (error: Event) => {
          console.error('SockJS error:', error);
          toast.error('Connection error occurred');
        };
        
        return socket;
      },
      connectHeaders: authType === 'JWT' && token && token !== 'SESSION_AUTH' ? {
        'Authorization': `Bearer ${token}`
      } : authType === 'SESSION' ? {
        'X-Requested-With': 'XMLHttpRequest'
      } : {},
      reconnectDelay: RECONNECT_DELAY,
      heartbeatIncoming: HEARTBEAT_INCOMING,
      heartbeatOutgoing: HEARTBEAT_OUTGOING,
      debug: function(str) {
        console.log('STOMP Debug:', str);
      },
      beforeConnect: () => {
        console.log('Attempting STOMP connection...');
      },
      onConnect: () => {
        console.log('STOMP client connected successfully');
        
        // Subscribe to notifications
        try {
          const broadcastSub = client.subscribe('/topic/notifications', (message: Message) => {
            console.log('=== RECEIVED BROADCAST NOTIFICATION ===');
            console.log('Message body:', message.body);
            try {
              const notification = JSON.parse(message.body) as NotificationMessage;
              console.log('Parsed notification:', notification);
              
              // Add notification to Redux store
              if (notification.id) {
                dispatch(addNotification({
                  id: notification.id,
                  title: notification.notificationType || 'Notification',
                  message: notification.message || message.body,
                  type: notification.notificationType === 'SUCCESS' ? 'SUCCESS' : 
                        notification.notificationType === 'WARNING' ? 'WARNING' : 
                        notification.notificationType === 'ERROR' ? 'ERROR' : 'INFO',
                  status: (notification.messageStatus as 'UNREAD' | 'READ' | 'DELIVERED') || 'UNREAD',
                  createdAt: notification.createdDate || new Date().toISOString(),
                  senderName: notification.senderName,
                  isRead: notification.messageStatus === 'READ',
                  isDelivered: notification.messageStatus === 'DELIVERED' || notification.messageStatus === 'READ',
                }));
                
                // Update unread count
                dispatch(fetchUnreadCount() as any);
              }
              
              // Show toast notification
              console.log('Showing toast for notification:', notification.message);
              toast.info(notification.message || message.body);
            } catch (e) {
              console.error('Error parsing notification:', e);
              toast.info(message.body);
            }
          }, {
            id: 'notifications-sub',
            ack: 'auto'
          });
          console.log('✅ Subscribed to /topic/notifications', broadcastSub);

          // Subscribe to user-specific notifications
          const userSub = client.subscribe('/user/queue/notifications', (message: Message) => {
            console.log('=== RECEIVED USER-SPECIFIC NOTIFICATION ===');
            console.log('Message body:', message.body);
            try {
              const notification = JSON.parse(message.body) as NotificationMessage;
              console.log('Parsed user notification:', notification);
              
              // Add notification to Redux store
              if (notification.id) {
                dispatch(addNotification({
                  id: notification.id,
                  title: notification.notificationType || 'Notification',
                  message: notification.message || message.body,
                  type: notification.notificationType === 'SUCCESS' ? 'SUCCESS' : 
                        notification.notificationType === 'WARNING' ? 'WARNING' : 
                        notification.notificationType === 'ERROR' ? 'ERROR' : 'INFO',
                  status: (notification.messageStatus as 'UNREAD' | 'READ' | 'DELIVERED') || 'UNREAD',
                  createdAt: notification.createdDate || new Date().toISOString(),
                  senderName: notification.senderName,
                  isRead: notification.messageStatus === 'READ',
                  isDelivered: notification.messageStatus === 'DELIVERED' || notification.messageStatus === 'READ',
                }));
                
                // Update unread count
                dispatch(fetchUnreadCount() as any);
              }
              
              // Show toast notification
              console.log('Showing toast for user notification:', notification.message);
              toast.info(`${notification.message || message.body}`);
            } catch (e) {
              console.error('Error parsing user notification:', e);
              toast.info(`${message.body}`);
            }
          }, {
            id: 'user-notifications-sub',
            ack: 'auto'
          });
          console.log('✅ Subscribed to /user/queue/notifications', userSub);
        } catch (error) {
          console.error('Error subscribing to topics:', error);
          toast.error('Failed to subscribe to notifications');
        }
      },
      onDisconnect: (frame) => {
        console.log('STOMP client disconnected:', frame);
      },
      onStompError: (frame) => {
        console.error('STOMP error:', frame);
        
        // Check if error is due to authentication
        const isAuthError = frame.headers?.message?.includes('Session closed') || 
                           frame.headers?.message?.includes('Authentication failed') ||
                           frame.headers?.message?.includes('Unauthorized');
        
        if (isAuthError) {
          authFailureCountRef.current += 1;
          console.warn(`WebSocket authentication failure #${authFailureCountRef.current}/${MAX_AUTH_FAILURES}`);
          
          // Stop retrying after too many auth failures
          if (authFailureCountRef.current >= MAX_AUTH_FAILURES) {
            console.error('WebSocket: Too many authentication failures, stopping reconnection attempts');
            toast.error('WebSocket connection failed: Authentication error. Please refresh the page.');
            if (clientRef.current) {
              clientRef.current.deactivate();
              clientRef.current = null;
            }
            return;
          }
          
          // Clear any existing reconnect timeout
          if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
          }
          
          // Increase delay with each failure (exponential backoff)
          const delay = RECONNECT_DELAY * authFailureCountRef.current;
          console.log(`WebSocket: Will retry connection in ${delay}ms (attempt ${authFailureCountRef.current}/${MAX_AUTH_FAILURES})`);
          
          // Attempt to reconnect after delay
          reconnectTimeoutRef.current = setTimeout(() => {
            if (clientRef.current) {
              console.log('Attempting to reconnect with fresh session...');
              clientRef.current.deactivate();
              clientRef.current.activate();
            }
          }, delay);
        } else {
          // Reset auth failure count on non-auth errors
          authFailureCountRef.current = 0;
        }
        
        if (!isAuthError) {
          toast.error('WebSocket communication error');
        }
      }
    });

    return client;
  }, [token, authType, shouldConnect, isAuthenticated, adminUser, dispatch]);

  useEffect(() => {
    const cleanup = () => {
      // Clear any pending reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Deactivate and cleanup client
      if (clientRef.current?.active) {
        console.log('Disconnecting WebSocket');
        clientRef.current.deactivate();
        clientRef.current = null;
      }
    };

    if (!shouldConnect || !isAuthenticated) {
      cleanup();
      return;
    }

    const newClient = createClient();
    if (newClient) {
      cleanup(); // Clean up old client before creating new one
      clientRef.current = newClient;
      console.log('Activating new WebSocket client');
      newClient.activate();
    }

    return cleanup;
  }, [createClient, shouldConnect, isAuthenticated]);

  return clientRef.current;
};